import { Component, ViewChild, inject } from '@angular/core';
import { DocumentPageBase } from '../../shared/components/document-page-base/document-page-base';
import { SettingsService } from '../../shared/settings/settings.service';
import { MatDialog } from '@angular/material/dialog';
import { LdwRouterService } from '../../route/ldw-router.service';
import { WebdokuService } from '../../web-doku/webdoku.service';
import { TranslateService } from '@ngx-translate/core';
import { DocumentService } from '../../shared/services/document.service';
import { ActivatedRoute, RouterStateSnapshot } from '@angular/router';
import { InfoService } from '../../shared/services/info.service';
import { BookmarkService } from '../../bookmarks/bookmark.service';
import { HistoryService } from '../../info/history.service';
import { DocumentViewComponent } from '../components';
import { of, take } from 'rxjs';

@Component({
    selector: 'ldw-document-page',
    templateUrl: './document-edit-page.component.html',
    styleUrls: ['./document-edit-page.component.css'],
    standalone: false
})
export class DocumentEditPageComponent extends DocumentPageBase {
  private historyService = inject(HistoryService);
  private settingsService = inject(SettingsService);

  @ViewChild('documentView') documentView!: DocumentViewComponent;

  constructor() {
    const dialog = inject(MatDialog);
    const routerService = inject(LdwRouterService);
    const webDokuService = inject(WebdokuService);
    const documentService = inject(DocumentService);
    const translate = inject(TranslateService);
    const infoService = inject(InfoService);
    const route = inject(ActivatedRoute);
    const bookmarkService = inject(BookmarkService);

    super(dialog, routerService, webDokuService, documentService, translate, infoService, route, bookmarkService);
  }

  get doCloseNotNavigate() {
    return this.settingsService.IsRunningInWindowModeAndOpenInNewTabMode();
  }

  get isReadOnly(): boolean {
    return !this.settingsService.settings.pdfSettings.isEditingEnabled();
  }

  get isStatusChangeAllowed(): boolean {
    return this.webDokuService.currentWebDokuRedliningAllowed;
  }

  protected updateHistory(webDokuId: string, docId: string) {
    this.historyService.openDocument(webDokuId, docId);
  }

  override close() {
    if (!this.isReadOnly) {
      this.documentView
        .checkCanClose()
        .pipe(take(1))
        .subscribe((canClose) => {
          if (!canClose) {
            return;
          }
          if (this.doCloseNotNavigate) {
            this.routerService.navigateClose();
            return;
          }
          this.routerService.canNavigateBack ? this.routerService.navigateBack() : this.routerService.navigateToDocumentsRoot();
        });
    }
  }

  checkChanges(nextState: RouterStateSnapshot | undefined) {
    if (this.isReadOnly) {
      // when document is readonly the default routing should take place
      return of(true);
    }

    // when we are already logged out, it makes no sense to check for changes
    // as changes could not be saved anyway due to invalid session/token
    if (nextState?.url === '/logoff') {
      return of(true);
    }

    // we are in edit mode, default routing is denied
    // therefore we have to provide the new route, if the user selects to discard changes or save
    return this.documentView.checkCanCloseNavigate(nextState);
  }
}
